import { isToday, isYesterday, format } from 'date-fns';

interface SpaceParticipant {
  userId: string;
  name: string;
  profileImageUrl: string | null;
}

interface SpaceWithParticipants {
  participants?: SpaceParticipant[];
}

export function hasUnknownParticipant(space: SpaceWithParticipants): boolean {
  return (space.participants ?? []).some((p) => p.name === "Unknown");
}

export function filterUnknownSpaces<T extends SpaceWithParticipants>(spaces: T[]): T[] {
  return spaces.filter((s) => !hasUnknownParticipant(s));
}

// --- Display name ---

/**
 * Computes the display name for a chat space, relative to the current user.
 *
 * For DMs: shows only the OTHER participant's name (not "Alice & Bob").
 * For group chats: shows the space's displayName.
 *
 * Participants can have varied shapes across the app:
 *   - { userId, name, isAgent?, displayName? }           (space list / sidebar)
 *   - { userId, isAgent?, displayName?, user?: { firstName, lastName } }  (enriched participants endpoint)
 *
 * @param space        — must include spaceType and displayName
 * @param participants — array of participant objects for this space
 * @param currentUserId — the logged-in user's ID
 * @param fallback     — fallback if nothing can be resolved (default: "Chat")
 */
export function getChatSpaceDisplayName(
  space: { spaceType?: string; displayName?: string | null },
  participants: any[],
  currentUserId: string | undefined,
  fallback = 'Chat',
): string {
  if (space.spaceType !== 'DIRECT_MESSAGE') {
    return space.displayName || fallback;
  }

  // For DMs, find the other participant (prefer agent, then non-current-user)
  const other = participants.find((p) => p.isAgent || (p.userId && p.userId !== currentUserId));
  if (!other) return space.displayName || fallback;

  // Resolve the best name from the participant
  if (other.isAgent) {
    return other.displayName || other.name || fallback;
  }
  // Enriched participant with nested user object
  if (other.user) {
    const full = `${other.user.firstName || ''} ${other.user.lastName || ''}`.trim();
    if (full) return full;
  }
  // Flat participant with name field
  if (other.name && other.name !== 'Unknown') return other.name;
  return space.displayName || fallback;
}

// --- Sorting ---

export function sortSpacesUnreadFirst<T extends { unreadCount?: number; updatedAt?: string | Date | null; lastMessage?: { createdAt?: string | Date | null } }>(
  spaces: T[]
): T[] {
  return [...spaces].sort((a, b) => {
    // 1. Unread spaces float to top
    const unreadDiff = Number((b.unreadCount ?? 0) > 0) - Number((a.unreadCount ?? 0) > 0);
    if (unreadDiff !== 0) return unreadDiff;
    // 2. Sort by most recent message (covers bot + human activity), fall back to updatedAt
    const aTime = new Date((a.lastMessage?.createdAt ?? a.updatedAt ?? 0) as string).getTime();
    const bTime = new Date((b.lastMessage?.createdAt ?? b.updatedAt ?? 0) as string).getTime();
    return bTime - aTime;
  });
}

// --- Date / time formatting ---

export function formatMessageDate(date: Date | string): string {
  const d = new Date(date);
  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'MMMM d');
}

export function formatMessageTime(date: Date | string): string {
  return format(new Date(date), 'h:mm a');
}

export function formatMessageTimestamp(date: Date | string): string {
  return `${formatMessageDate(date)}, ${formatMessageTime(date)}`;
}

// --- Relative time (moved from global-chat-button.tsx) ---

export function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return `${Math.floor(diffDays / 7)}w`;
}
